import { useState } from 'react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Checkbox } from '~/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';

export interface GuardianAccountStatusProps {
  personId: string;
  email?: string | null;
  hasUserAccount: boolean;
  accountInviteStatus?: string | null;
  accountInviteEmail?: string | null;
  accountInviteExpiresAt?: string | null;
  accountInviteNoticeStatus?: string | null;
  accountInviteNoticeReason?: string | null;
  accountInviteNoticeAt?: string | null;
  accountInviteNoticeOutcomeUnknown?: boolean;
  onInvite: (personId: string, email?: string, confirmUnknown?: boolean) => void;
  onRevoke: (personId: string) => void;
  busy?: boolean;
}

export function GuardianAccountStatus({
  personId,
  email,
  hasUserAccount,
  accountInviteStatus,
  accountInviteEmail,
  accountInviteExpiresAt,
  accountInviteNoticeStatus,
  accountInviteNoticeReason,
  accountInviteNoticeAt,
  accountInviteNoticeOutcomeUnknown,
  onInvite,
  onRevoke,
  busy,
}: GuardianAccountStatusProps) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptEmail, setPromptEmail] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  if (hasUserAccount) {
    return <div className="mt-3 text-xs text-muted-foreground">✓ Account active</div>;
  }

  if (accountInviteStatus === 'PENDING') {
    const expires = accountInviteExpiresAt
      ? new Date(accountInviteExpiresAt).toLocaleDateString()
      : '';
    return (
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground" title={accountInviteNoticeReason ?? undefined}>
          Invited{accountInviteEmail ? ` (${accountInviteEmail})` : ''}
          {expires ? ` · expires ${expires}` : ''}
          <span className="block">
            {accountInviteNoticeStatus ? `Email: ${accountInviteNoticeStatus}` : 'Email delivery not tracked'}
            {accountInviteNoticeAt ? ` · ${new Date(accountInviteNoticeAt).toLocaleString()}` : ''}
          </span>
          {accountInviteNoticeReason && <span className="block">{accountInviteNoticeReason}</span>}
        </span>
        <div className="flex gap-1">
          <Button type="button" variant="ghost" size="sm" disabled={busy || accountInviteNoticeStatus === 'Queued'} onClick={() => {
            if (accountInviteNoticeOutcomeUnknown) { setConfirmed(false); setConfirmOpen(true); }
            else onInvite(personId);
          }}>
            Resend
          </Button>
          <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => onRevoke(personId)}>
            Revoke
          </Button>
        </div>
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Resend account invitation?</DialogTitle>
              <DialogDescription>The previous send may have reached the recipient. Check email-provider activity before sending again. The existing invitation link stays valid.</DialogDescription>
            </DialogHeader>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={confirmed} onCheckedChange={(value) => setConfirmed(value === true)} />
              I checked email-provider activity and approve sending again.
            </label>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button disabled={busy || !confirmed} onClick={() => { onInvite(personId, undefined, true); setConfirmOpen(false); }}>Queue invitation</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (!email && showPrompt) {
    return (
      <div className="mt-3 space-y-2">
        <Input
          type="email"
          placeholder="parent@example.com"
          value={promptEmail}
          onChange={(e) => setPromptEmail(e.target.value)}
        />
        <div className="flex gap-2">
          <Button type="button" size="sm" disabled={busy || !promptEmail} onClick={() => onInvite(personId, promptEmail)}>
            Send invite
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowPrompt(false)}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={() => (email ? onInvite(personId, email) : setShowPrompt(true))}
      >
        Invite to create account
      </Button>
    </div>
  );
}
